import React, { useLayoutEffect, useRef, useState } from "react";
import EditableText from "./EditableText";
import { useAssetUrl } from "./assets";
import { updateCell, updateItem } from "./proposalOps";
import { EmbedsContext, TwinView } from "./Twin";
import {
  C, CONTENT, DISPLAY, FONT, FOOTER_TOP, H, INNOWIDE, LOGO, LOGO_RATIO, W, contentLimit, coverTitleSize, euro, longDate, unitLine,
  titleFit, weeks,
} from "./theme";
import "./proposal.css";

const abs = (left, top, width, height) => ({ position: "absolute", left, top, width, height });

// Repères de la charte : encarts arrondis bleu pâle, étiquettes en capitales bleues, intertitres en Montserrat.
const RADIUS = 10;
const LABEL = { fontFamily: DISPLAY, fontSize: 13, fontWeight: 700, color: C.blue, letterSpacing: 1.2, textTransform: "uppercase" };
const HEADING = { fontFamily: DISPLAY, fontWeight: 700 };

// Lots du chiffrage lié, numérotés dans leur ordre.
const lotIndex = (estimate) => {
  const map = {};
  (estimate?.lots || []).forEach((lot, index) => { map[lot.id] = { ...lot, number: index + 1 }; });
  return map;
};

const hasDiscount = (estimate) => Number(estimate?.client_discount) > 0;

const ZONE_HEIGHT = CONTENT.bottom - CONTENT.top;
const GAP = 20;

// Place occupée par les blocs d'une slide : la somme des hauteurs et des espacements, indépendamment du pied de lot
// calé en bas. Les blocs se placent dans la zone utile (y = 165 à 660) mais peuvent descendre jusqu'à la limite
// (`bottom`, voir contentLimit : le bas du canevas) : il n'y a débordement qu'au-delà.
export const measureContent = (element, bottom = CONTENT.bottom) => {
  const children = [...element.children];
  const used = Math.round(children.reduce((sum, child) => sum + child.offsetHeight, 0)
    + GAP * Math.max(0, children.length - 1));
  const room = bottom - CONTENT.top;
  return { used_px: used, free_px: room - used, overflow_px: Math.max(0, used - room), room_px: room };
};

// Zone visuelle de la mise en page « visual » : illustration pleine hauteur sur un côté.
const VISUAL_WIDTH = 460;
const VISUAL_GAP = 40;

const Visual = ({ slide, edit, onSlide }) => {
  const visual = slide.visual || {};
  const left = slide.visual_side === "left" ? CONTENT.left : CONTENT.left + CONTENT.width - VISUAL_WIDTH;
  const setCaption = (value) => onSlide({ visual: { ...visual, caption: value } }, "visual.caption");
  return (
    <div style={{ ...abs(left, CONTENT.top, VISUAL_WIDTH, ZONE_HEIGHT), display: "flex", flexDirection: "column", gap: 8 }}>
      {visual.embed ? (
        // Jumeau dans la zone visuelle : l'image de la zone devient son image fixe (PDF, miniatures).
        <>
          <TwinView embed={visual.embed} poster={visual.image} style={{ flex: 1, minHeight: 0 }} />
          {(visual.caption || edit) && (
            <EditableText value={visual.caption} onChange={setCaption} editable={edit} singleLine rich={false}
              placeholder="Légende" label="Légende du jumeau" style={{ fontSize: 13, color: C.muted }} />
          )}
        </>
      ) : visual.image ? (
        <>
          <AssetImage asset={visual.image} alt={visual.caption} style={{ width: "100%", flex: 1, minHeight: 0 }} />
          {(visual.caption || edit) && (
            <EditableText value={visual.caption} onChange={setCaption} editable={edit} singleLine rich={false}
              placeholder="Légende" label="Légende de l'illustration" style={{ fontSize: 13, color: C.muted }} />
          )}
        </>
      ) : (
        <div style={{ flex: 1, background: C.pale, border: `1.5px solid ${C.line}`, borderRadius: RADIUS, display: "flex",
          flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 24, textAlign: "center" }}>
          <div style={LABEL}>ILLUSTRATION À INSÉRER</div>
          <EditableText value={visual.caption} onChange={setCaption} editable={edit} rich={false}
            placeholder="Brief : ce que l'image doit montrer" label="Brief de l'illustration"
            style={{ fontSize: 14, color: C.muted, lineHeight: 1.3 }} />
        </div>
      )}
    </div>
  );
};

const Missing = ({ children }) => (
  <div style={{ padding: "10px 14px", border: `1.5px dashed ${C.line}`, color: C.muted, fontSize: 15 }}>{children}</div>
);

const AssetImage = ({ asset, alt, style, placeholder }) => {
  const url = useAssetUrl(asset);
  if (!asset || !url) return placeholder || null;
  return <img src={url} alt={alt || ""} style={{ objectFit: "contain", display: "block", ...style }} />;
};

// ------------------------------------------------------------------- blocs
// Image à côté d'un texte ou d'un encart (photo produit, schéma) : le texte garde le reste de la largeur.
const Beside = ({ block, edit, optional, onBlock, children }) => {
  if (!block.image) return children;
  return (
    <div style={{ display: "flex", gap: 24, alignItems: "flex-start",
      flexDirection: block.image_side === "left" ? "row-reverse" : "row" }}>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      <figure style={{ width: block.image_width || 240, margin: 0, flexShrink: 0 }}>
        <AssetImage asset={block.image} alt={block.image_caption || block.label}
          style={{ width: "100%", height: "auto", maxHeight: 300 }} />
        {(block.image_caption || optional) && (
          <EditableText value={block.image_caption} editable={edit} singleLine rich={false} placeholder="Légende"
            label="Légende de l'image" style={{ fontSize: 13, color: C.muted, marginTop: 4 }}
            onChange={(value) => onBlock({ image_caption: value }, `${block.id}.image_caption`)} />
        )}
      </figure>
    </div>
  );
};

const TextBlock = (props) => {
  const { block } = props;
  const inner = <TextBody {...props} />;
  if (block.style === "box") {
    return (
      <div style={{ background: C.pale, borderRadius: RADIUS, padding: "18px 26px 20px" }}>
        <Beside {...props}>{inner}</Beside>
      </div>
    );
  }
  return <Beside {...props}>{inner}</Beside>;
};

const TextBody = ({ block, edit, optional, onBlock }) => {
  const set = (field) => (value) => onBlock({ [field]: value }, `${block.id}.${field}`);
  if (block.style === "box") {
    return (
      <div>
        {(block.label || optional) && (
          <EditableText value={block.label} onChange={set("label")} editable={edit} singleLine rich={false}
            placeholder="Intertitre de l'encart" label="Intertitre de l'encart"
            style={{ ...HEADING, fontSize: 15, color: C.blue, marginBottom: 8 }} />
        )}
        <EditableText value={block.text} onChange={set("text")} editable={edit} placeholder="Texte de l'encart"
          label="Texte de l'encart" style={{ fontSize: 17, color: C.black, lineHeight: 1.3 }} />
      </div>
    );
  }
  if (block.style === "note") {
    return <EditableText value={block.text} onChange={set("text")} editable={edit} placeholder="Mention"
      label="Mention" style={{ fontSize: 14, color: C.muted, lineHeight: 1.3 }} />;
  }
  return (
    <div>
      {(block.label || optional) && (
        <EditableText value={block.label} onChange={set("label")} editable={edit} singleLine rich={false}
          placeholder="Intertitre (facultatif)" label="Intertitre"
          style={{ ...HEADING, fontSize: 20, color: C.blue, marginBottom: 8 }} />
      )}
      <EditableText value={block.text} onChange={set("text")} editable={edit}
        placeholder="Texte. Une ligne « - … » devient une puce." label="Texte"
        style={{ fontSize: 18, color: C.body, lineHeight: 1.3 }} />
    </div>
  );
};

const LotPriceLine = ({ lot, discount }) => (
  <div style={{ marginTop: 10 }}>
    {weeks(lot.delay_weeks) && (
      <div style={{ fontSize: 14, fontWeight: 700, color: C.muted }}>{weeks(lot.delay_weeks)}</div>
    )}
    <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
      <span style={{ ...HEADING, fontSize: 28, color: C.blue }}>
        {euro(lot.net)} HT{discount ? " remisé" : ""}
      </span>
      {discount && <span style={{ fontSize: 15, color: C.muted }}>au lieu de {euro(lot.gross)} HT</span>}
    </div>
    {unitLine(lot, discount) && <div style={{ fontSize: 14, color: C.muted }}>{unitLine(lot, discount)}</div>}
  </div>
);

const ColumnsBlock = ({ block, edit, optional, onBlock, lots, discount }) => {
  const setItem = (index, field) => (value) =>
    onBlock((current) => updateItem(current, index, { [field]: value }), `${block.id}.${index}.${field}`);
  const cards = block.style === "cards";
  const cells = [];
  block.items.forEach((item, index) => {
    if (cards && block.arrows && index > 0) {
      cells.push(
        <div key={`arrow-${index}`} aria-hidden="true"
          style={{ alignSelf: "center", fontSize: 26, fontWeight: 700, color: C.blue, textAlign: "center" }}>→</div>,
      );
    }
    const lot = item.lot_id ? lots[item.lot_id] : null;
    cells.push(
      <div key={index} style={cards ? { background: C.pale, borderRadius: RADIUS, padding: "14px 18px 16px", minHeight: 150 } : {}}>
        {cards && (item.tag || optional) && (
          <EditableText value={item.tag} onChange={setItem(index, "tag")} editable={edit} singleLine rich={false}
            placeholder="ÉTIQUETTE" label="Étiquette"
            style={{ ...LABEL, marginBottom: 4 }} />
        )}
        {(item.label || optional) && (
          <EditableText value={item.label} onChange={setItem(index, "label")} editable={edit} singleLine rich={false}
            placeholder="Intertitre" label="Intertitre de colonne"
            style={cards
              ? { ...HEADING, fontSize: 18, color: C.black, marginBottom: 6 }
              : { ...HEADING, fontSize: 20, color: C.blue, marginBottom: 10 }} />
        )}
        {item.image && (
          <div style={{ marginBottom: 8 }}>
            <AssetImage asset={item.image} alt={item.caption || item.label} style={{ width: "100%", height: 160 }} />
            {(item.caption || optional) && (
              <EditableText value={item.caption} onChange={setItem(index, "caption")} editable={edit} singleLine
                rich={false} placeholder="Légende" label="Légende" style={{ fontSize: 13, color: C.muted, marginTop: 4 }} />
            )}
          </div>
        )}
        <EditableText value={item.text} onChange={setItem(index, "text")} editable={edit} placeholder="Texte"
          label="Texte de colonne"
          style={cards ? { fontSize: 15, color: C.body, lineHeight: 1.25 } : { fontSize: 17, color: C.body, lineHeight: 1.3 }} />
        {item.lot_id && (lot ? <LotPriceLine lot={lot} discount={discount} /> : <Missing>Lot absent du chiffrage lié.</Missing>)}
      </div>,
    );
  });
  const template = block.items.map(() => "1fr").join(cards && block.arrows ? " 40px " : " ");
  return <div style={{ display: "grid", gridTemplateColumns: template, gap: cards ? (block.arrows ? 0 : 24) : 50 }}>{cells}</div>;
};

const ImageBlock = ({ block, edit, optional, onBlock }) => {
  const set = (value) => onBlock({ caption: value }, `${block.id}.caption`);
  const placeholder = (
    <div style={{
      height: block.height, background: C.pale, border: `1.5px solid ${C.line}`, borderRadius: RADIUS, display: "flex",
      flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 24, textAlign: "center",
    }}>
      <div style={LABEL}>ILLUSTRATION À INSÉRER</div>
      <EditableText value={block.caption} onChange={set} editable={edit} rich={false}
        placeholder="Brief : ce que l'image doit montrer" label="Brief de l'illustration"
        style={{ fontSize: 14, color: C.muted, lineHeight: 1.3 }} />
    </div>
  );
  if (!block.image) return placeholder;
  return (
    <div>
      <AssetImage asset={block.image} alt={block.caption} style={{ width: "100%", height: block.height }} placeholder={placeholder} />
      {(block.caption || optional) && (
        <EditableText value={block.caption} onChange={set} editable={edit} singleLine rich={false}
          placeholder="Légende" label="Légende" style={{ fontSize: 13, color: C.muted, marginTop: 6 }} />
      )}
    </div>
  );
};

// Jumeau numérique (page HTML autonome) : manipulable sur la slide ouverte et en présentation, image fixe ailleurs.
const TwinBlock = ({ block, edit, optional, onBlock }) => (
  <div style={{ position: "relative" }}>
    <TwinView embed={block.embed} poster={block.poster} style={{ height: block.height || 360 }} />
    {edit && (
      // L'iframe garde les clics pour elle : cette étiquette sélectionne le bloc.
      <div style={{ ...LABEL, position: "absolute", left: 10, top: 10, background: C.white, padding: "3px 8px",
        borderRadius: 6, boxShadow: "0 1px 3px rgba(0,0,0,.15)", cursor: "pointer" }}>Jumeau 3D</div>
    )}
    {(block.caption || optional) && (
      <EditableText value={block.caption} onChange={(value) => onBlock({ caption: value }, `${block.id}.caption`)}
        editable={edit} singleLine rich={false} placeholder="Légende" label="Légende du jumeau"
        style={{ fontSize: 13, color: C.muted, marginTop: 6 }} />
    )}
  </div>
);

const Figure = ({ label, value, labelColor = C.muted, valueStyle }) => (
  <div>
    <div style={{ fontSize: 13, fontWeight: 700, color: labelColor }}>{label}</div>
    <div style={{ ...HEADING, color: C.blue, lineHeight: 1.15, ...valueStyle }}>{value}</div>
  </div>
);

// Pied de lot : délai, puis le prix remisé mis en avant et le prix HT en retrait.
const LotPriceBlock = ({ block, edit, optional, onBlock, lots, discount }) => {
  const lot = lots[block.lot_id];
  if (!lot) return <Missing>Pied de lot : le lot « {block.lot_id} » n'existe pas dans le chiffrage lié.</Missing>;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 40 }}>
      <div style={{ width: 230 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>Délai indicatif</div>
        {/* Sans libellé saisi, le délai vient du chiffrage. */}
        <EditableText value={block.delay_label || weeks(lot.delay_weeks)}
          onChange={(value) => onBlock({ delay_label: value }, `${block.id}.delay`)}
          editable={edit} singleLine rich={false} placeholder="Délai" label="Délai affiché"
          style={{ ...HEADING, fontSize: 28, color: C.blue, lineHeight: 1.15 }} />
      </div>
      {discount && (
        <Figure label="Prix HT" value={euro(lot.gross)}
          valueStyle={{ fontFamily: FONT, fontSize: 20, color: C.muted, fontWeight: 400 }} />
      )}
      <div>
        <Figure label={discount ? "Prix HT remisé" : "Prix HT"} labelColor={C.black} value={euro(lot.net)}
          valueStyle={{ fontSize: 34 }} />
        {block.unit_price !== false && unitLine(lot, discount) && (
          <div style={{ fontSize: 14, color: C.muted, marginTop: 2 }}>{unitLine(lot, discount)}</div>
        )}
      </div>
      <div style={{ flex: 1, paddingLeft: 10 }}>
        {(block.note || optional) && (
          <EditableText value={block.note} onChange={(value) => onBlock({ note: value }, `${block.id}.note`)}
            editable={edit} rich={false} placeholder="Prérequis ou réserve (facultatif)" label="Note du pied de lot"
            style={{ fontSize: 14, color: C.muted, lineHeight: 1.25 }} />
        )}
      </div>
    </div>
  );
};

// Tableaux du deck : en-tête bleu, filets bleu clair, lignes alternées.
const cell = { border: `1px solid ${C.line}`, padding: "8px 10px", verticalAlign: "middle" };
const headRow = { background: C.blue, color: C.white };
const rowBackground = (index) => (index % 2 ? C.white : C.pale);

// Total du devis = somme des prix affichés, arrondis à l'euro, ligne par ligne : l'acheteur retrouve le total en
// additionnant les lignes (57 814 € et non 57 817 €, total remisé arrondi d'un coup ; demande du 15/10).
export const lineSum = (lots, key) => lots.reduce((sum, lot) => sum + Math.round(Number(lot[key]) || 0), 0);

const QuoteBlock = ({ block, edit, optional, onBlock, estimate, lots, discount }) => {
  const ids = block.lot_ids || (estimate?.lots || []).map((lot) => lot.id);
  const rows = ids.map((lotId) => lots[lotId]).filter(Boolean);
  const required = rows.filter((lot) => !lot.optional);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {(block.intro || optional) && (
        <EditableText value={block.intro} onChange={(value) => onBlock({ intro: value }, `${block.id}.intro`)}
          editable={edit} placeholder="Introduction du devis" label="Introduction du devis"
          style={{ fontSize: 18, color: C.body, lineHeight: 1.3 }} />
      )}
      {!estimate ? <Missing>Aucun chiffrage lié : le devis se remplit à partir d'un chiffrage de l'affaire.</Missing> : (
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 15, color: C.black }}>
          <thead>
            <tr style={{ ...headRow, fontSize: 16 }}>
              <th style={{ ...cell, textAlign: "left" }}>Description</th>
              <th style={{ ...cell, textAlign: "right", width: 160 }}>Délai</th>
              <th style={{ ...cell, textAlign: "right", width: 200 }}>Prix HT</th>
              {discount && <th style={{ ...cell, textAlign: "right", width: 220 }}>Prix HT remisé</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((lot, index) => (
              <tr key={lot.id} style={{ background: rowBackground(index) }}>
                <td style={cell}>
                  {block.lot_prefix === false ? "" : `Lot ${lot.number} · `}{lot.name}{lot.optional ? " (option)" : ""}
                  {block.unit_price !== false && unitLine(lot, discount) && (
                    <div style={{ fontSize: 13, color: C.muted }}>{unitLine(lot, discount)}</div>
                  )}
                </td>
                <td style={{ ...cell, textAlign: "right" }}>{weeks(lot.delay_weeks)}</td>
                <td style={{ ...cell, textAlign: "right", ...(discount ? { color: C.muted } : { fontSize: 18, fontWeight: 700, color: C.blue }) }}>
                  {euro(discount ? lot.gross : lot.net)}
                </td>
                {discount && <td style={{ ...cell, textAlign: "right", fontSize: 18, fontWeight: 700, color: C.blue }}>{euro(lot.net)}</td>}
              </tr>
            ))}
            {block.show_total && required.length > 0 && (
              <tr style={{ background: C.line, fontWeight: 700, color: C.black }}>
                <td style={cell}>Total{rows.length > required.length ? " hors options" : ""}</td>
                <td style={cell} />
                <td style={{ ...cell, textAlign: "right" }}>{euro(lineSum(required, discount ? "gross" : "net"))}</td>
                {discount && <td style={{ ...cell, textAlign: "right" }}>{euro(lineSum(required, "net"))}</td>}
              </tr>
            )}
          </tbody>
        </table>
      )}
      {(block.footer || optional) && (
        <EditableText value={block.footer} onChange={(value) => onBlock({ footer: value }, `${block.id}.footer`)}
          editable={edit} placeholder="Précisions : ordre de commande, délais, validité…" label="Précisions du devis"
          style={{ fontSize: 16, color: C.body, lineHeight: 1.3 }} />
      )}
    </div>
  );
};

const TableBlock = ({ block, edit, onBlock }) => (
  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 16, color: C.black }}>
    <tbody>
      {block.rows.map((cells, row) => (
        <tr key={row} style={row === 0 ? headRow : { background: rowBackground(row) }}>
          {cells.map((value, column) => (
            <td key={column} style={{ ...cell, fontWeight: row === 0 ? 700 : 400, color: row === 0 ? C.white : C.black }}>
              <EditableText value={value} editable={edit} rich={false} label={`Cellule ${row + 1}.${column + 1}`}
                placeholder="…" style={{ fontSize: 16 }}
                onChange={(text) => onBlock((current) => updateCell(current, row, column, text), `${block.id}.${row}.${column}`)} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const BLOCKS = {
  text: TextBlock, columns: ColumnsBlock, image: ImageBlock, lot_price: LotPriceBlock, quote: QuoteBlock, table: TableBlock,
  twin: TwinBlock,
};

// --------------------------------------------------------------- mises en page
// Couverture claire, comme les slides : logos Innowide et client en haut, nature du document en capitales bleues,
// titre noir souligné d'un filet bleu, référence et date dans une bande bleu pâle en bas.
const Cover = ({ slide, meta, edit, onSlide, onMeta }) => {
  const clientLogo = useAssetUrl(meta.client_logo);
  const reference = [meta.reference && `n° ${meta.reference}`, meta.version].filter(Boolean).join(" ");
  const client = { ...abs(CONTENT.left + CONTENT.width - 300, 46, 300, 72) };
  return (
    <>
      <img src={LOGO} alt="Logo Innowide" style={abs(CONTENT.left, 54, 56 * LOGO_RATIO, 56)} />
      {clientLogo
        ? <img src={clientLogo} alt="Logo client" style={{ ...client, objectFit: "contain", objectPosition: "right center" }} />
        : edit && (
          <div style={{ ...client, border: `1.5px dashed ${C.line}`, color: C.muted, fontSize: 14, display: "flex",
            alignItems: "center", justifyContent: "center", textAlign: "center" }}>
            Logo client : à ajouter dans les réglages
          </div>
        )}
      {/* Filet bleu à gauche, de la hauteur du bloc : la nature du document, le titre et le sous-titre. */}
      <div style={{ ...abs(CONTENT.left - 28, 222, 1128), borderLeft: `6px solid ${C.blue}`, paddingLeft: 22,
        display: "flex", flexDirection: "column", gap: 18 }}>
        <EditableText value={meta.kicker} onChange={(value) => onMeta({ kicker: value }, "meta.kicker")} editable={edit}
          singleLine rich={false} label="Nature du document"
          placeholder={meta.document === "presentation" ? "Présentation" : "Proposition technique et commerciale"}
          style={{ ...HEADING, fontSize: 21, color: C.blue, letterSpacing: 3.5, textTransform: "uppercase", lineHeight: "36px",
            marginBottom: -4 }} />
        <EditableText value={slide.title} onChange={(value) => onSlide({ title: value }, "title")} editable={edit}
          singleLine rich={false} placeholder="Titre de la proposition" label="Titre de la couverture"
          style={{ ...HEADING, fontSize: coverTitleSize(slide.title), color: C.black, lineHeight: 1.15 }} />
        {(slide.subtitle || edit) && (
          <EditableText value={slide.subtitle} onChange={(value) => onSlide({ subtitle: value }, "subtitle")} editable={edit}
            rich={false} placeholder="Ce que le projet apporte, en une phrase" label="Sous-titre de la couverture"
            style={{ fontSize: 28, color: C.muted, lineHeight: 1.3 }} />
        )}
      </div>
      <div style={{ ...abs(0, 600, W, H - 600), background: C.pale }} />
      <div style={{ ...abs(CONTENT.left, 645, CONTENT.width), fontSize: 21, color: C.muted }}>
        {[reference, longDate(meta.date)].filter(Boolean).join("  •  ")}
      </div>
    </>
  );
};

// Points client (assemblage de présentations, voir PresentationAssembly) : intercalaire léger qui remplace la
// couverture d'une présentation, et sommaire du point. Mises en page lues seulement, jamais éditées sur la slide.
const Divider = ({ slide }) => (
  <>
    <img src={LOGO} alt="Logo Innowide" style={abs(CONTENT.left, 54, 40 * LOGO_RATIO, 40)} />
    <div style={{ ...abs(CONTENT.left, 262, 220, 200), ...HEADING, fontSize: 150, lineHeight: "200px", color: C.line }}>
      {slide.tag}
    </div>
    <div style={{ ...abs(CONTENT.left + 250, 268, CONTENT.width - 250), borderLeft: `6px solid ${C.blue}`, paddingLeft: 26,
      display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ ...LABEL, fontSize: 18, letterSpacing: 3 }}>
        Sujet {Number(slide.tag) || ""}{slide.total ? ` sur ${slide.total}` : ""}
      </div>
      <div style={{ ...HEADING, fontSize: titleFit(slide.title).size + 12, color: C.black, lineHeight: 1.15 }}>{slide.title}</div>
      {slide.subtitle && <div style={{ fontSize: 26, color: C.muted, lineHeight: 1.3 }}>{slide.subtitle}</div>}
    </div>
    <div style={{ ...abs(0, H - 40, W, 40), background: C.pale }} />
  </>
);

const Agenda = ({ slide }) => {
  const items = slide.items || [];
  const row = Math.min(96, Math.floor((CONTENT.bottom - CONTENT.top) / Math.max(items.length, 1)));
  return (
    <div style={{ ...abs(CONTENT.left, CONTENT.top, CONTENT.width, CONTENT.bottom - CONTENT.top) }}>
      {items.map((item) => (
        <div key={item.number} style={{ height: row, display: "flex", alignItems: "center", gap: 28,
          borderBottom: `1px solid ${C.line}` }}>
          <div style={{ ...HEADING, width: 64, fontSize: Math.min(36, row * 0.45), color: C.blue }}>
            {String(item.number).padStart(2, "0")}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ ...HEADING, fontSize: Math.min(24, row * 0.3), color: C.black, whiteSpace: "nowrap", overflow: "hidden",
              textOverflow: "ellipsis" }}>{item.label}</div>
            {item.text && item.text !== item.label && (
              <div style={{ fontSize: Math.min(18, row * 0.24), color: C.muted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden",
                textOverflow: "ellipsis" }}>{item.text}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const Label = ({ children, style }) => (
  <div style={{ ...HEADING, fontSize: 20, color: C.blue, ...style }}>{children}</div>
);

const Conditions = ({ meta, estimate, edit, onMeta }) => {
  const body = { fontSize: 17, color: C.body, whiteSpace: "pre-line", lineHeight: 1.35 };
  const lots = (estimate?.lots || []).map((lot, index) => `☐ Lot ${index + 1}`).join("     ");
  return (
    <>
      <div style={abs(CONTENT.left, 165, 340)}>
        <Label>Émetteur</Label>
        <div style={{ ...body, marginTop: 12 }}>{`${INNOWIDE.name}\n${INNOWIDE.address}\n${INNOWIDE.contact}`}</div>
      </div>
      <div style={abs(452, 165, 400)}>
        <Label>Référence et validité</Label>
        <div style={{ ...body, marginTop: 12 }}>
          {`Réf. ${[meta.reference, meta.version].filter(Boolean).join(" ")}\nDate d'émission : ${longDate(meta.date)}\n`
            + `Validité : ${longDate(meta.validity_date)}\nType : études, services et livraison de biens`}
        </div>
      </div>
      <div style={abs(895, 165, 300)}>
        <Label>Conditions de règlement</Label>
        <div style={{ marginTop: 12 }}>
          <EditableText value={meta.payment_terms} onChange={(value) => onMeta({ payment_terms: value }, "meta.payment_terms")}
            editable={edit} rich={false} placeholder="Conditions de règlement" label="Conditions de règlement" style={body} />
        </div>
      </div>
      <div style={{ ...abs(CONTENT.left, 400, CONTENT.width), display: "flex", gap: 20, fontSize: 18, color: C.body }}>
        <span style={{ fontWeight: 700, color: C.black, fontSize: 17 }}>Éléments sélectionnés :</span>
        <span style={{ whiteSpace: "pre" }}>{lots || "…"}</span>
      </div>
      <div style={{ ...abs(CONTENT.left, 455, 700), fontSize: 18, fontWeight: 700, color: C.black }}>
        Montant total HT retenu : __________________ €
      </div>
      <div style={{ ...abs(CONTENT.left, 525, 800), fontSize: 15, color: C.muted }}>
        Date, nom, qualité et signature précédés de la mention « Bon pour accord » :
      </div>
      <div style={{ ...abs(CONTENT.left, 590, 780), borderBottom: `1.5px solid ${C.line}` }} />
      <div style={{ ...abs(CONTENT.left, 645, 1060), fontSize: 11, color: C.muted, whiteSpace: "pre" }}>{INNOWIDE.legal}</div>
    </>
  );
};

// ------------------------------------------------------- en-tête et pied de page
// Comme dans le deck : étiquette en capitales bleues (la pastille de la slide), titre Montserrat noir sur une ligne,
// logo en haut à droite ; en bas, la référence de l'offre et le numéro de page en gris.
const LOGO_HEIGHT = 30;
const TITLE_BOTTOM = 118;

const SlideHeader = ({ slide, edit, onSlide, highlight }) => {
  const fit = titleFit(slide.title);
  return (
    <>
      <img src={LOGO} alt="Innowide"
        style={abs(CONTENT.left + CONTENT.width - LOGO_HEIGHT * LOGO_RATIO, 34, LOGO_HEIGHT * LOGO_RATIO, LOGO_HEIGHT)} />
      {slide.tag && (
        <div data-offer-tag style={{ ...abs(CONTENT.left, 31, 1000, 36), display: "flex", alignItems: "center",
          fontFamily: DISPLAY, fontSize: 18, fontWeight: 700, color: C.blue, letterSpacing: 2.6, textTransform: "uppercase" }}>
          {slide.tag}
        </div>
      )}
      {/* Calé en bas : un titre trop long pour une ligne passe sur deux lignes vers le haut, sans toucher le sous-titre. */}
      <div style={{ ...abs(CONTENT.left, 30, CONTENT.width, TITLE_BOTTOM - 30), display: "flex", alignItems: "flex-end" }}
        data-offer-title data-title-lines={fit.oneLine ? 1 : 2} className={highlight?.title ? "offer-changed" : undefined}>
        <div style={{ width: "100%" }}>
          <EditableText value={slide.title} onChange={(value) => onSlide({ title: value }, "title")} editable={edit}
            singleLine rich={false} placeholder="Titre : une phrase qui affirme quelque chose" label="Titre de la slide"
            style={{ ...HEADING, fontSize: fit.size, color: C.black, lineHeight: fit.oneLine ? "48px" : "26px",
              ...(fit.oneLine ? { whiteSpace: "nowrap" } : {}) }} />
        </div>
      </div>
      {(slide.subtitle || (edit && ["content", "visual"].includes(slide.layout))) && (
        <div style={abs(CONTENT.left, 122, CONTENT.width)}>
          <EditableText value={slide.subtitle} onChange={(value) => onSlide({ subtitle: value }, "subtitle")}
            editable={edit} singleLine rich={false} placeholder="Sous-titre : la nuance, la réserve"
            label="Sous-titre de la slide" style={{ fontSize: 17, color: C.muted, lineHeight: "22px" }} />
        </div>
      )}
    </>
  );
};

// Pied de page : « Proposition P-2026-134 v2 · innowide.fr » ; pour une présentation, son titre (Rémy, 29/10 : pas
// « Présentation 126 »).
const footerText = (meta, title) => {
  if (meta?.document === "presentation") return [title, INNOWIDE.site].filter(Boolean).join(" · ");
  const reference = [meta?.reference, meta?.version].filter(Boolean).join(" ");
  return [meta?.reference && `Proposition ${reference}`, INNOWIDE.site].filter(Boolean).join(" · ");
};

const SlideFooter = ({ meta, page, title }) => (
  <>
    <div style={{ ...abs(CONTENT.left, FOOTER_TOP, 900, 14), fontSize: 12, lineHeight: "14px", color: C.muted,
      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {footerText(meta, title)}
    </div>
    {page != null && (
      <div style={{ ...abs(CONTENT.left + CONTENT.width - 80, FOOTER_TOP, 80, 14), fontSize: 12, lineHeight: "14px",
        color: C.muted, textAlign: "right" }}>{page}</div>
    )}
  </>
);

// ------------------------------------------------------------------- la slide
const SlideCanvas = ({
  slide, meta: deckMeta, estimate, page, edit = false, selectedBlockId, onSelectBlock,
  onSlide = () => {}, onBlock = () => {}, onMeta = () => {}, showOverflow = false, highlight = null,
  embeds = {}, live = false, deckTitle: title = "",
}) => {
  // Slide d'un point client : elle garde le pied de page et la couverture de sa présentation d'origine.
  const meta = slide.deck_meta || deckMeta;
  const deckTitle = slide.deck_title || title;
  // Blocs modifiés par quelqu'un d'autre : ils s'illuminent quelques secondes (voir proposal.css).
  const changed = (blockId) => Boolean(highlight?.blocks?.includes(blockId));
  const twinRef = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const lots = lotIndex(estimate);
  const discount = hasDiscount(estimate);

  // Débordement de la zone utile (y = 165 à 660) : signalé dans l'éditeur, jamais à l'impression. Mesuré sur une copie
  // invisible de la slide telle qu'elle sera livrée : sans les invites des champs vides ni les zones de saisie
  // ouvertes, qui ajoutent de la hauteur à l'écran d'édition seulement (même mesure que la vérification de Nexus).
  useLayoutEffect(() => {
    const element = twinRef.current?.querySelector("[data-offer-content]");
    if (!showOverflow || !element) {
      setOverflow(false);
      return undefined;
    }
    const limit = contentLimit(slide);
    const check = () => setOverflow(measureContent(element, limit).overflow_px > 2);
    check();
    // Une image qui se charge change la hauteur d'un bloc : on remesure.
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(check);
    [...element.children].forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [showOverflow, slide, estimate, meta]);

  return (
    <EmbedsContext.Provider value={{ embeds, live }}>
    <div
      className="offer-slide"
      onClick={() => onSelectBlock?.(null)}
      style={{
        position: "relative", width: W, height: H, overflow: "hidden", fontFamily: FONT, color: C.body,
        background: C.white,
      }}
    >
      {slide.layout === "cover" && <Cover slide={slide} meta={meta} edit={edit} onSlide={onSlide} onMeta={onMeta} />}
      {slide.layout === "divider" && <Divider slide={slide} />}
      {slide.layout === "agenda" && <Agenda slide={slide} />}
      {!["cover", "divider"].includes(slide.layout) && (
        <SlideHeader slide={slide} edit={edit} onSlide={onSlide} highlight={highlight} />
      )}
      {slide.layout === "conditions" && <Conditions meta={meta} estimate={estimate} edit={edit} onMeta={onMeta} />}
      {slide.layout === "visual" && <Visual slide={slide} edit={edit} onSlide={onSlide} />}
      {(slide.layout === "content" || slide.layout === "visual") && (
        // Le contenu qui dépasse reste visible (sur la marge basse) : on voit ce qui ne tient pas.
        <div data-offer-content style={{
          ...(slide.layout === "visual"
            ? abs(slide.visual_side === "left" ? CONTENT.left + VISUAL_WIDTH + VISUAL_GAP : CONTENT.left, CONTENT.top,
              CONTENT.width - VISUAL_WIDTH - VISUAL_GAP, ZONE_HEIGHT)
            : abs(CONTENT.left, CONTENT.top, CONTENT.width, ZONE_HEIGHT)),
          display: "flex", flexDirection: "column", gap: GAP, overflow: "visible",
        }}>
          {(slide.blocks || []).map((block) => {
            const Block = BLOCKS[block.type];
            const selected = edit && selectedBlockId === block.id;
            // Champs facultatifs vides (intertitre, légende, note…) : proposés sur le bloc sélectionné seulement.
            return (
              <div
                key={block.id}
                className={[edit && "offer-block", changed(block.id) && "offer-changed"].filter(Boolean).join(" ") || undefined}
                data-selected={selected || undefined}
                onClick={edit ? (event) => { event.stopPropagation(); onSelectBlock?.(block.id); } : undefined}
                style={{ position: "relative", ...(block.type === "lot_price" ? { marginTop: "auto" } : {}) }}
              >
                {Block && (
                  <Block block={block} edit={edit} optional={selected} lots={lots} discount={discount} estimate={estimate}
                    onBlock={(patch, key) => onBlock(block.id, patch, key)} />
                )}
              </div>
            );
          })}
        </div>
      )}
      {!["cover", "divider"].includes(slide.layout) && <SlideFooter meta={meta} page={page} title={deckTitle} />}
      {showOverflow && (
        <div ref={twinRef} aria-hidden="true" data-offer-measure
          style={{ position: "absolute", left: 0, top: 0, visibility: "hidden", pointerEvents: "none" }}>
          <SlideCanvas slide={slide} meta={meta} estimate={estimate} embeds={embeds} deckTitle={deckTitle} />
        </div>
      )}
      {showOverflow && overflow && (
        <div style={{ ...abs(0, contentLimit(slide), W, 4), background: "#DC2626" }}
          title="Le contenu sort de la slide">
          <span style={{ position: "absolute", right: 90, top: -30, background: "#DC2626", color: "#FFFFFF",
            fontSize: 16, fontWeight: 700, padding: "3px 10px" }}>Contenu trop long pour la slide</span>
        </div>
      )}
    </div>
    </EmbedsContext.Provider>
  );
};

export default SlideCanvas;
